import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { useParams } from 'react-router';
import { API, Auth } from 'aws-amplify';
import { editTextReset } from 'src/store/modules/editText';
import { imageReset } from 'src/store/modules/imageUpload';
import setInitValue from 'src/utils/setInitValue';
import { changeEnter } from 'src/utils/changeEnter';
import { editUserCompany } from 'src/store/modules/client';
import { trackingReset } from 'src/store/modules/trackings';
import { getFontURL } from 'src/utils/getFontUrl';
import LightModeClose from '../Icon/LightModeClose';
import { initTrackingValues } from '../TemplateTracking/BottomSlideUpTracking';
import { switchReset } from 'src/store/modules/switchButton';
import { buttonsReset } from 'src/store/modules/buttons';

const initViewValues = {
  bg: 'color',
  bgOpacity: { bgDimmed: 0 },
  bgColor: { background: '#000000' },
  bgImage: {
    logo: 'https://image.inappmessage.com/sample/coins.png'
  },
  radius: { background: 4, logo: 4 },
  deeplink: { content: 'https://www.inappmessage.com/' },
  text: {
    title: 'Allow push notifications and get notified of sales !'
  },
  fontSize: { title: 0.9375 },
  fontWeight: { title: 500 },
  color: { title: '#ffffff' },
  fontFamily: { title: 'Pretendard' },
  textAlign: { title: 'left' },

  button1: {
    content: [],
    close_button: [],
    background: []
  },
  button2: {
    content: [],
    close_button: [],
    background: []
  },
  swtichPosition: 'up',
  switchDarkMode: true
};

const BottomSlideUp: FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    async function getUserId() {
      try {
        const current = await Auth.currentUserInfo();
        dispatch(editUserCompany(current.attributes['custom:Company']));
      } catch (error) {
        console.log('error');
      }
    }
    getUserId();
  }, []);

  let bgImage = useSelector((state: RootState) => state.imageUpload.bgImage);
  let bgColor = useSelector((state: RootState) => state.imageUpload.bgColor);
  let radius = useSelector((state: RootState) => state.imageUpload.radius);

  let text = useSelector((state: RootState) => state.editText.text);
  let fontSize = useSelector((state: RootState) => state.editText.fontSize);
  let fontWeight = useSelector((state: RootState) => state.editText.fontWeight);
  let color = useSelector((state: RootState) => state.editText.color);
  let fontFamily = useSelector((state: RootState) => state.editText.fontFamily);
  let textAlign = useSelector((state: RootState) => state.editText.textAlign);

  let switchPosition = useSelector(
    (state: RootState) => state.switchButton.switchPosition
  );
  const titleFontURL = getFontURL(fontFamily?.title);

  const style = `  
  @font-face {
	  font-family: 'title';
	  src: url(${titleFontURL});
	}
	
  .root-view {
    width: 100%;
    height: 100%;
    
    position: relative;
    background: none;
    overflow: hidden;
  }

  .background {
    width: 100%;
    height: 100%;
              
    position: absolute;
    top: 0;
    background: rgba(0, 0, 0, 0.1);
    }
    
  .wrapper {
   	width: 100%;
		height: 80px;
		position: absolute;
		bottom: 63px;
		display: flex;
		justify-content: center;
  }
  
  .content-wrapper {
   	display: flex;
		width: calc(100% - 30px);						
		max-width: 480px;
		height: 100%;
		padding: 15px;
		margin: 0 15px;		
		border: none;
		background: ${bgColor.background};
		border-radius: ${radius.background}px;	
  } 

  .image-wrapper {
		width: 50px;
		height: 50px;
		flex-shrink: 0;
	}

	.image {
		width: 100%;
		height: 100%;		
		border-radius: ${radius?.logo}px;
	}

	.message-wrapper {
		width: 100%;
		height: 100%;		
		display: flex;
		align-items: center;
    overflow: hidden;
    white-space: normal;            
		text-overflow: ellipsis;
	}

	.message {
		width: 100%;		
		font-size: ${fontSize?.title}rem;
		 font-family: ${
       ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
         fontFamily?.title
       )
         ? fontFamily?.title
         : 'title'
     };
		font-weight: ${fontWeight?.title};
    text-align: ${textAlign?.title};
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
    white-space: normal;              
		text-overflow: ellipsis;
		letter-spacing: -0.3px;
		line-height: 1.27;
		margin-left: 10px;
		margin-right: 15px;
		color: ${color?.title};
	}

	.button-wrapper {  
		height: 100%;
		display: flex;
		align-items: center;
	}

  .slide-down {
    animation: slideDown 0.2s ease-out forwards;
  }
  .slide-up {
		animation: slideUp 0.2s ease-out forwards;
	}
  .top-slide-down {		
		animation: topSlideDown 0.2s ease-out forwards;
	}
	.top-slide-up {		
		animation: topSlideUp 0.2s ease-out forwards;
	}
  @keyframes slideDown {
    from {
      bottom: 63px;
    }
    to {
      bottom: -100%;
    }
}

  @keyframes slideUp {
		from {
			bottom: -100%;
		}
		to {
			bottom: 63px;
		}
	}
  @keyframes topSlideDown {
		from {
			top: -100px;
		}
		to {
			top: 45px;
		}
	}
	@keyframes topSlideUp {
		from {
			top: 45px;
		}
		to {
			top: -100px;
		}
	}				
  `;

  const { id } = useParams<{ id: string }>();
  const [isVisible, setIsVisible] = useState(true);
  const handleClose = () => {
    setIsVisible(prev => !prev);
  };
  const getModalClass = () => {
    if (switchPosition === 'up') {
      return isVisible ? 'slide-up' : 'slide-down';
    } else {
      return isVisible ? 'top-slide-down' : 'top-slide-up';
    }
  };
  useEffect(() => {
    async function onLoad() {
      try {
        if (id) {
          const { customValues } = await API.get(
            'iam-editor',
            `/templates/${id}`,
            ''
          );
          setInitValue(dispatch, customValues);
        }
      } catch (error) {}
    }
    setInitValue(dispatch, { ...initViewValues, ...initTrackingValues });
    onLoad();

    return () => {
      dispatch(editTextReset());
      dispatch(imageReset());
      dispatch(trackingReset());
      dispatch(buttonsReset());
      dispatch(switchReset());
    };
  }, []);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: style }}></style>
      <div className="root-view">
        <div className="background" onClick={handleClose}></div>
        <div className={`wrapper ${getModalClass()}`}>
          <div className="content-wrapper">
            <div className="image-wrapper">
              <img className="image" src={bgImage?.logo} />
            </div>
            <div className="message-wrapper">
              <div className="message">{changeEnter(text?.title)}</div>
            </div>
            <div className="button-wrapper">
              <LightModeClose />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default BottomSlideUp;
